import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useFeature, useFeaturesReady } from '../EnvironmentContext';
import type { FeatureKey } from '../featureFlags';

/**
 * Wraps a route whose feature may be off.
 *
 * Conditionally *registering* the `<Route>` instead is the tempting shortcut, and it is wrong for
 * anything reachable by URL. Flags read false until `GET /api/features` settles, so during that
 * window the route does not exist; there is no catch-all route in this app, so a cold deep link to
 * a perfectly enabled page renders a blank screen until the flags arrive, and renders a blank
 * screen permanently if the request fails. Keeping the route registered and deciding inside it
 * means the URL always resolves to something deliberate.
 *
 * A disabled feature renders the same not-found message any unknown URL would, deliberately: the
 * server returns 404 rather than 403 so that a disabled feature is indistinguishable from one that
 * was never built, and the client should not undo that by announcing "this feature is turned off".
 *
 * Visibility only. The server enforces the same flag independently, so a user who forces this
 * component to render the page gains a view and a series of 404s, never access.
 */
const FeatureRoute: React.FC<{ flag: FeatureKey; children: React.ReactElement }> = ({ flag, children }) => {
  const ready = useFeaturesReady();
  const enabled = useFeature(flag);

  if (!ready) {
    return (
      <Box
        role="status"
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8, flexDirection: 'column', gap: 2 }}
      >
        <CircularProgress aria-label="Loading" />
      </Box>
    );
  }

  if (!enabled) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" component="h2" gutterBottom>
          Page not found
        </Typography>
        <Typography variant="body2" color="text.secondary">
          The page you asked for does not exist. Check the address, or pick a tool from the menu.
        </Typography>
      </Box>
    );
  }

  return children;
};

export default FeatureRoute;
